import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

// LEAFLET IMPORTS
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, AttributionControl } from 'react-leaflet';
import L from 'leaflet';

// Fix icônes Leaflet dans React
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34]
});
L.Marker.prototype.options.icon = DefaultIcon;

import {
  FileClock,
  CalendarDays,
  HardHat,
  Users,
  BarChart3,
  ArrowRight,
  FolderKanban,
  UserPlus,
  CalendarRange,
  PenTool,
  LayoutDashboard,
  Briefcase,
  TrendingUp,
  MapPin,
  AlertTriangle,
  Archive,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  Calendar,
  GraduationCap,
  FolderSearch,
  Bell,
  Clock,
  Bot,
  Receipt
} from 'lucide-react';

// --- TYPES ---
interface DashboardStats {
  pendingHours: number;
  pendingMaterials: number;
  activeProjects: number;
  totalEmployees: number;
}

interface ProjectStat {
  id: string;
  code: string;
  name: string;
  budget: number;
  consumed: number;
  percentage: number;
}

interface AbsenceItem {
  name: string;
  type: string;
  days: number;
}

interface WorkingItem {
  name: string;
  project: string;
  projectName?: string; 
  period: string;
  lat?: number;
  lng?: number;
}

interface DailyStatus {
  absent: AbsenceItem[];
  working: WorkingItem[];
}

interface LeaveQueryResult {
  type: string;
  start_date: string;
  end_date: string;
  employee: { id: string; full_name: string } | null;
}

interface TrainingDayQueryResult {
  day: string;
  employee: { id: string; full_name: string } | null;
}

interface AssignmentQueryResult {
  period: string;
  assignment_date: string;
  employee: { id: string; full_name: string } | null;
  project: { code: string; name: string; lat: number | null; lng: number | null } | null;
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  bg: string;
  loading: boolean;
  onClick: () => void;
  actionLabel: string;
  hideValue?: boolean;
}

interface ActionCardProps {
  icon: React.ReactNode;
  title: string;
  desc: string;
  onClick: () => void;
  color?: string;
}

interface AlertCardProps {
  count: number;
  label: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  borderColor: string;
  onClick: () => void;
}

// --- HELPERS DATES ---
const getStartOfWeek = (d: Date) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
};

const getEndOfWeek = (d: Date) => {
  const date = getStartOfWeek(d);
  date.setDate(date.getDate() + 6);
  return date;
};

const getWeekNumber = (d: Date): number => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const getInitials = (name: string | undefined | null) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatDateDB = (d: Date) => d.toISOString().split('T')[0];

const getAbsenceStyle = (type: string) => {
  switch (type) {
    case 'cp': return { label: 'Congés Payés', color: '#166534', bg: '#dcfce7', border: '#86efac' }; 
    case 'rtt': return { label: 'RTT', color: '#1e40af', bg: '#dbeafe', border: '#93c5fd' }; 
    case 'maladie': return { label: 'Arrêt Maladie', color: '#991b1b', bg: '#fee2e2', border: '#fca5a5' }; 
    case 'sans_solde': return { label: 'Sans Solde', color: '#92400e', bg: '#fef3c7', border: '#fcd34d' }; 
    case 'formation': return { label: 'Formation', color: '#6b21a8', bg: '#f3e8ff', border: '#d8b4fe' }; 
    default: return { label: 'Autre', color: '#334155', bg: '#f1f5f9', border: '#cbd5e1' }; 
  }
};

// --- NOUVEL HELPER DE FUSION DES PÉRIODES ---
const computeMergedPeriodStyle = (periods: string[]) => {
  const pSet = new Set(periods.map(p => p.toUpperCase()));

  const hasMorning = pSet.has('AM') || pSet.has('Q1') || pSet.has('Q2');
  const hasAfternoon = pSet.has('PM') || pSet.has('Q3') || pSet.has('Q4');
  const hasFullDay = pSet.has('DAY');

  // CAS 1 : Journée Complète
  if (hasFullDay || (hasMorning && hasAfternoon)) {
    return { label: 'Journée', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1', sortOrder: 0 };
  }

  // CAS 2 : Matin Uniquement
  if (hasMorning) {
    return { label: 'Matin', bg: '#fff7ed', color: '#ea580c', border: '#fed7aa', sortOrder: 1 };
  }

  // CAS 3 : Après-midi Uniquement
  if (hasAfternoon) {
    return { label: 'Après-midi', bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', sortOrder: 2 };
  }

  // Fallback
  return { label: '?', bg: '#f1f5f9', color: '#94a3b8', border: '#e2e8f0', sortOrder: 3 };
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // États Globaux
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({ pendingHours: 0, pendingMaterials: 0, activeProjects: 0, totalEmployees: 0 });
  const [projects, setProjects] = useState<ProjectStat[]>([]);

  // États Météo / Carte
  const [daily, setDaily] = useState<DailyStatus>({ absent: [], working: [] });
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
  const [currentDate, setCurrentDate] = useState(new Date());

  // Calcul des bornes de dates pour la requête
  const dateRange = useMemo(() => {
    if (viewMode === 'day') {
      const d = formatDateDB(currentDate);
      return { start: d, end: d, label: currentDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) };
    } else {
      const start = getStartOfWeek(currentDate);
      const end = getEndOfWeek(currentDate);
      const weekNum = getWeekNumber(start);
      return {
        start: formatDateDB(start),
        end: formatDateDB(end),
        label: `Semaine ${weekNum}`
      };
    }
  }, [viewMode, currentDate]);

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        // 1. KPIs
        const [hoursRes, materialsRes, projectsRes, empsRes] = await Promise.all([
          supabase.from('weekly_sheets').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
          supabase.from('material_requests').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
          supabase.from('projects')
            .select('*', { count: 'exact', head: true })
            .eq('is_active', true)
            .neq('code', 'SYS-FORM'),
          supabase.from('employees').select('*', { count: 'exact', head: true }).eq('is_active', true)
        ]);

        setStats({
          pendingHours: hoursRes.count || 0,
          pendingMaterials: materialsRes.count || 0,
          activeProjects: projectsRes.count || 0,
          totalEmployees: empsRes.count || 0
        });

        // 2. RENTABILITÉ (Top 5)
        const { data: projData } = await supabase
          .from('projects')
          .select('id, code, name, estimated_hours')
          .eq('is_active', true)
          .eq('category', 'production')
          .neq('code', 'SYS-FORM') // Sécurité aussi ici
          .order('code', { ascending: false })
          .limit(5);

        if (projData) {
          const { data: timeData } = await supabase
            .from('time_entries')
            .select('project_id, hours_normal, hours_saturday, hours_major')
            .in('project_id', projData.map(p => p.id));

          const computedStats = projData.map(p => {
            const entries = timeData?.filter(t => t.project_id === p.id) || [];
            const consumed = entries.reduce((acc, curr) => acc + curr.hours_normal + curr.hours_saturday + curr.hours_major, 0);
            return {
              ...p,
              budget: p.estimated_hours,
              consumed,
              percentage: p.estimated_hours > 0 ? (consumed / p.estimated_hours) * 100 : 0
            };
          }).sort((a, b) => b.percentage - a.percentage);

          setProjects(computedStats);
        }

        // 3. MÉTÉO & CARTE
        const [leavesResult, workResult, trainingResult] = await Promise.all([
          supabase
            .from('leaves')
            .select('type, start_date, end_date, employee:employees!employee_id(id, full_name)')
            .eq('status', 'validated')
            .or(`start_date.lte.${dateRange.end},end_date.gte.${dateRange.start}`),
          supabase
            .from('project_assignments')
            .select('assignment_date, period, employee:employees(id, full_name), project:projects(code, name, lat, lng)')
            .gte('assignment_date', dateRange.start)
            .lte('assignment_date', dateRange.end),
          supabase
            .from('training_days')
            .select('day, employee:employees(id, full_name)')
            .gte('day', dateRange.start)
            .lte('day', dateRange.end)
        ]);

        const leavesData = leavesResult.data;
        const workData = workResult.data;
        const trainingData = trainingResult.data;

        // A. On prépare la "Liste Noire" (Jours où l'employé est absent)
        const absenceMap = new Set<string>();
        const safeLeaves = (leavesData as unknown as LeaveQueryResult[]) || [];
        const safeTrainings = (trainingData as unknown as TrainingDayQueryResult[]) || [];

        safeLeaves.forEach(leave => {
          if (!leave.employee?.id) return;
          const currentDateLoop = new Date(leave.start_date);
          const endDateLoop = new Date(leave.end_date);
          while (currentDateLoop <= endDateLoop) {
            const dateStr = currentDateLoop.toISOString().split('T')[0];
            absenceMap.add(`${leave.employee.id}_${dateStr}`);
            currentDateLoop.setDate(currentDateLoop.getDate() + 1);
          }
        });

        // Ajouter les formations à la liste noire
        safeTrainings.forEach(training => {
          if (!training.employee?.id) return;
          absenceMap.add(`${training.employee.id}_${training.day}`);
        });

        // B. On prépare la liste des Absents (REGROUPÉE par nom+type) avec comptage des jours
        const absenceGroupMap = new Map<string, AbsenceItem>();
        const rangeStart = new Date(dateRange.start);
        const rangeEnd = new Date(dateRange.end);

        // Ajouter les congés avec comptage des jours dans la période
        safeLeaves.forEach(l => {
          const name = l.employee?.full_name || 'Inconnu';
          const key = `${name}_${l.type}`;

          // Calculer le nombre de jours dans la période sélectionnée
          const leaveStart = new Date(l.start_date);
          const leaveEnd = new Date(l.end_date);
          const effectiveStart = leaveStart > rangeStart ? leaveStart : rangeStart;
          const effectiveEnd = leaveEnd < rangeEnd ? leaveEnd : rangeEnd;
          const daysInPeriod = Math.max(0, Math.floor((effectiveEnd.getTime() - effectiveStart.getTime()) / 86400000) + 1);

          if (absenceGroupMap.has(key)) {
            const existing = absenceGroupMap.get(key)!;
            existing.days += daysInPeriod;
          } else {
            absenceGroupMap.set(key, { name, type: l.type, days: daysInPeriod });
          }
        });

        // Ajouter les formations (chaque entrée = 1 jour)
        safeTrainings.forEach(t => {
          const name = t.employee?.full_name || 'Inconnu';
          const key = `${name}_formation`;
          if (absenceGroupMap.has(key)) {
            const existing = absenceGroupMap.get(key)!;
            existing.days += 1;
          } else {
            absenceGroupMap.set(key, { name, type: 'formation', days: 1 });
          }
        });

        const absentList = Array.from(absenceGroupMap.values());

        // C. On traite les travailleurs en FILTRANT grâce à la Liste Noire
        const safeWork = (workData as unknown as AssignmentQueryResult[]) || [];
        const workingList = safeWork
          .filter(w => {
            if (!w.employee?.id) return false;
            const key = `${w.employee.id}_${w.assignment_date}`;
            return !absenceMap.has(key);
          })
          .map(w => ({
            name: w.employee?.full_name || 'Inconnu',
            project: w.project?.code || '?',
            projectName: w.project?.name || '',
            period: w.period,
            lat: w.project?.lat || undefined,
            lng: w.project?.lng || undefined
          }));

        setDaily({
          absent: absentList,
          working: workingList
        });

      } catch (error) {
        console.error("Erreur chargement dashboard", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [dateRange]);

  // Grouper les présences par chantier pour l'affichage (AVEC DÉDOUBLONNAGE)
  const groupedPresence = useMemo(() => {
    // 1. On regroupe d'abord les données brutes
    const groups: Record<string, { 
      code: string, 
      name: string, 
      rawPeople: Record<string, string[]>, // Map temporaire : Nom -> Liste des périodes (ex: "Jean": ["AM", "PM"])
      lat?: number, 
      lng?: number 
    }> = {};
    
    daily.working.forEach(item => {
      // On ignore le projet de formation dans l'affichage carte/liste du jour
      if (item.project === 'SYS-FORM') return;

      const key = item.project;
      if (!groups[key]) {
        groups[key] = { 
          code: item.project, 
          name: item.projectName || '', 
          rawPeople: {}, // Initialisation
          lat: item.lat,
          lng: item.lng
        };
      }

      // Si la personne n'est pas encore dans ce chantier, on l'init
      if (!groups[key].rawPeople[item.name]) {
        groups[key].rawPeople[item.name] = [];
      }
      // On ajoute la période (ex: "AM") à sa liste
      groups[key].rawPeople[item.name].push(item.period);
    });

    // 2. On transforme les données brutes en liste finale nettoyée pour l'affichage
    return Object.values(groups).map(group => {
      const consolidatedPeople = Object.entries(group.rawPeople).map(([name, periods]) => {
        // C'est ici qu'on fusionne : Si ["AM", "PM"] -> devient "Journée"
        const styleInfo = computeMergedPeriodStyle(periods);
        return {
          name,
          ...styleInfo
        };
      });

      return {
        ...group,
        people: consolidatedPeople // Ceci remplace rawPeople
      };
    });
  }, [daily.working]);

  const handleDateChange = (dir: number) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + dir);
    } else {
      newDate.setDate(newDate.getDate() + (dir * 7));
    }
    setCurrentDate(newDate);
  };

  // --- FONCTION POUR CRÉER L'ICÔNE MAISON (SIÈGE) ---
  const createHomeIcon = () => {
    const homeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`;
    const html = `
      <div style="
        background-color: #2563eb; 
        color: white;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid white;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      ">
        ${homeSvg}
      </div>
    `;
    return L.divIcon({
      className: 'custom-home-icon',
      html: html,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      popupAnchor: [0, -18]
    });
  };

  // --- FONCTION POUR CRÉER L'ICÔNE AVATAR SUR LA CARTE ---
  const createMapIcon = (people: {name: string}[]) => {
    const firstPerson = people[0]?.name || '?';
    // Le count est maintenant correct car `groupedPresence` dédoublonne les personnes
    const count = people.length;
    const initials = getInitials(firstPerson);
    
    const badgeHtml = count > 1 
      ? `<div style="position: absolute; top: -4px; right: -4px; background: #ef4444; color: white; width: 18px; height: 18px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; border: 2px solid white; font-weight: 700;">+${count-1}</div>` 
      : '';

    const html = `
      <div style="
        background-color: #2563eb; 
        color: white;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'Inter', sans-serif;
        font-weight: 700;
        font-size: 13px;
        border: 2px solid white;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        position: relative;
      ">
        ${initials}
        ${badgeHtml}
      </div>
    `;

    return L.divIcon({
      className: 'custom-map-icon',
      html: html,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      popupAnchor: [0, -18]
    });
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', maxWidth: '1600px', margin: '0 auto', paddingBottom:'40px' }}>
      
      {/* HEADER */}
      <div style={{ marginBottom: '30px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h1 style={{ marginBottom: '6px', fontSize: '1.8rem', color: '#1e293b' }}>Tableau de Bord</h1>
          <p style={{ color: '#64748b', margin: 0 }}>Bonjour <strong>{user?.full_name}</strong>, voici la situation aujourd'hui.</p>
        </div>
        <div style={{ background: '#eff6ff', color: '#2563eb', padding: '8px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: '600', display:'flex', alignItems:'center', gap:'8px' }}>
          <CalendarDays size={18} />
          {new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* SECTION NOTIFICATIONS & ALERTES — uniquement ce qui concerne la direction */}
      {(stats.pendingMaterials > 0 || stats.pendingHours > 0) && (
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '1.1rem', color: '#b91c1c', display:'flex', alignItems:'center', gap:'8px', fontWeight: '600', marginBottom: '16px' }}>
            <span style={{ display: 'inline-flex', animation: 'pulse 2s infinite' }}><Bell size={20} /></span> À Traiter Prioritairement
          </h2>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            {stats.pendingMaterials > 0 && (
              <AlertCard
                count={stats.pendingMaterials} label="Demandes Matériel" icon={<ShoppingCart size={20}/>}
                color="#991b1b" bg="#fee2e2" borderColor="#ef4444" onClick={() => navigate('/admin/materials')}
              />
            )}
            {stats.pendingHours > 0 && (
              <AlertCard
                count={stats.pendingHours} label="Feuilles d'heures" icon={<Clock size={20}/>}
                color="#1d4ed8" bg="#dbeafe" borderColor="#3b82f6" onClick={() => navigate('/admin')}
              />
            )}
          </div>
        </div>
      )}


      {/* 1. LIGNE KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <StatCard
          title="Planificateur" value={0} icon={<PenTool size={24} />}
          color="#2563eb" bg="#eff6ff" loading={loading} onClick={() => navigate('/admin/planning-edit')} actionLabel="Ouvrir"
          hideValue
        />
        <StatCard
          title="Reporting & Paie" value={0} icon={<BarChart3 size={24} />}
          color="#10b981" bg="#ecfdf5" loading={loading} onClick={() => navigate('/admin/reporting')} actionLabel="Consulter"
          hideValue
        />
        <StatCard
          title="Chantiers Actifs" value={stats.activeProjects} icon={<HardHat size={24} />}
          color="#3b82f6" bg="#eff6ff" loading={loading} onClick={() => navigate('/admin/projects')} actionLabel="Voir liste"
        />
        <StatCard
          title="Effectif Total" value={stats.totalEmployees} icon={<Users size={24} />}
          color="#8b5cf6" bg="#f5f3ff" loading={loading} onClick={() => navigate('/admin/employees')} actionLabel="Gérer"
        />
      </div>

      {/* 2. SECTION PRINCIPALE */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        
        {/* COLONNE GAUCHE (CARTE & MÉTÉO) */}
        <div style={{ flex: '2 1 600px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', display:'flex', flexDirection:'column' }}>
            
            {/* TOOLBAR CONTROLE CARTE */}
            <div style={{ padding: '15px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent:'space-between', flexWrap:'wrap', gap:'15px', background:'#f8fafc' }}>
              
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                <div style={{ background: '#dbeafe', padding: '8px', borderRadius: '8px', color: '#1e40af' }}><MapPin size={20} /></div>
                <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Localisation des équipes</h2>
              </div>

              {/* CONTROLES DATE & VUE */}
              <div style={{ display:'flex', alignItems:'center', gap:'12px', background:'white', padding:'4px', borderRadius:'10px', border:'1px solid #e2e8f0' }}>
                <div style={{ display:'flex', background:'#f1f5f9', borderRadius:'8px', padding:'2px' }}>
                  <button onClick={() => { setViewMode('day'); setCurrentDate(new Date()); }} style={{ padding:'6px 12px', borderRadius:'6px', border:'none', fontSize:'0.8rem', fontWeight:'600', cursor:'pointer', background: viewMode==='day'?'white':'transparent', color: viewMode==='day'?'#2563eb':'#64748b', boxShadow: viewMode==='day'?'0 1px 2px rgba(0,0,0,0.1)':'none' }}>Jour</button>
                  <button onClick={() => { setViewMode('week'); setCurrentDate(new Date()); }} style={{ padding:'6px 12px', borderRadius:'6px', border:'none', fontSize:'0.8rem', fontWeight:'600', cursor:'pointer', background: viewMode==='week'?'white':'transparent', color: viewMode==='week'?'#2563eb':'#64748b', boxShadow: viewMode==='week'?'0 1px 2px rgba(0,0,0,0.1)':'none' }}>Semaine</button>
                </div>

                <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'0 8px', borderLeft:'1px solid #e2e8f0' }}>
                  <button onClick={() => handleDateChange(-1)} style={{ background:'transparent', border:'none', cursor:'pointer', color:'#64748b' }}><ChevronLeft size={18} /></button>
                  <span style={{ 
                    fontSize:'0.9rem', 
                    fontWeight:'700', 
                    color:'#1e293b', 
                    width:'200px', 
                    display: 'block', 
                    textAlign:'center', 
                    textTransform:'capitalize',
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {dateRange.label}
                  </span>
                  <button onClick={() => handleDateChange(1)} style={{ background:'transparent', border:'none', cursor:'pointer', color:'#64748b' }}><ChevronRight size={18} /></button>
                </div>

                <button onClick={() => setCurrentDate(new Date())} title="Revenir à aujourd'hui" style={{ padding:'6px', background:'#eff6ff', borderRadius:'6px', border:'none', cursor:'pointer', color:'#2563eb', marginRight:'4px' }}>
                  <Calendar size={16} />
                </button>
              </div>
            </div>

            {/* A. CARTE LEAFLET */}
            <div style={{ height: '400px', width: '100%', position: 'relative', borderBottom:'1px solid #f1f5f9', zIndex: 0 }}>
              <MapContainer 
                center={[47.962, 0.203]} 
                zoom={9} 
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
                attributionControl={false}
              >
                <AttributionControl prefix={false} />
                <TileLayer
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='© OSM'
                />
                
                <Marker position={[48.02919338187648, 0.1759264190688933 ]} icon={createHomeIcon()}>
                  <Popup><strong>Siège OGER ÉNERGIES</strong><br />22 rue André Citroen</Popup>
                </Marker>

                {groupedPresence.map((site) => (
                  site.lat && site.lng ? (
                    <Marker 
                      key={site.code} 
                      position={[site.lat, site.lng]}
                      icon={createMapIcon(site.people)}
                    >
                      <Popup>
                        <div style={{ textAlign:'center' }}>
                          <strong style={{ color:'#2563eb' }}>{site.code}</strong><br/>
                          <span style={{ fontSize:'0.85rem' }}>{site.name}</span>
                          <hr style={{ margin:'5px 0', border:0, borderTop:'1px solid #eee' }} />
                          {/* Le nombre est maintenant correct (dédoublonné) */}
                          <div style={{ fontWeight:'bold', marginBottom:'4px' }}>{site.people.length} Pers.</div>
                          <ul style={{ paddingLeft:'15px', margin:0, textAlign:'left', fontSize:'0.8rem' }}>
                            {site.people.map((p, idx) => (
                              <li key={idx}>
                                {p.name} <span style={{fontSize:'0.7rem', color:'#64748b'}}>({p.label})</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </Popup>
                    </Marker>
                  ) : null
                ))}
              </MapContainer>
            </div>

            {/* B. LISTE DES AFFECTATIONS */}
            <div style={{ padding:'20px', background:'#fff' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'15px' }}>
                <h3 style={{ fontSize:'0.9rem', textTransform:'uppercase', color:'#64748b', margin:0, display:'flex', alignItems:'center', gap:'6px' }}>
                  <HardHat size={16} /> Affectations ({viewMode === 'day' ? 'Jour' : 'Semaine'})
                </h3>
              </div>
              
              {daily.working.length === 0 ? (
                <p style={{ fontSize:'0.9rem', color:'#94a3b8', fontStyle:'italic' }}>Aucune affectation planifiée sur cette période.</p>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:'16px' }}>
                  {groupedPresence.map((group, i) => (
                    <div key={i} style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'12px', padding:'16px', display:'flex', flexDirection:'column', gap:'10px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        <div>
                          <div style={{ fontWeight:'700', color:'#1e293b', fontSize:'1.1rem' }}>{group.name || group.code}</div>
                          <div style={{ fontSize:'0.75rem', color:'#64748b', fontWeight:'500' }}>{group.code}</div>
                        </div>
                        {group.lat ?
                          <span style={{ color:'#10b981', background:'#ecfdf5', padding:'2px 6px', borderRadius:'6px', fontSize:'0.7rem', fontWeight:'600' }}><MapPin size={10} /> Carte OK</span> :
                          <span style={{ color:'#94a3b8', background:'#f1f5f9', padding:'2px 6px', borderRadius:'6px', fontSize:'0.7rem' }}>Sans GPS</span>
                        }
                      </div>
                      
                      {/* LISTE DES PERSONNES DÉDOUBLONNÉE AVEC BADGE */}
                      <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                        {group.people
                          .sort((a, b) => a.sortOrder - b.sortOrder)
                          .map((person, idx) => (
                            <div key={idx} style={{ 
                              fontSize:'0.85rem', color:'#334155', background:'white', 
                              padding:'4px 8px', borderRadius:'6px', border:'1px solid #e2e8f0', boxShadow:'0 1px 2px rgba(0,0,0,0.02)',
                              display: 'flex', alignItems: 'center', gap: '6px'
                            }}>
                              <span>{person.name}</span>
                              <span style={{ 
                                fontSize:'0.65rem', 
                                fontWeight:'700', 
                                textTransform:'uppercase',
                                background: person.bg,
                                color: person.color,
                                border: `1px solid ${person.border}`,
                                padding: '1px 4px',
                                borderRadius: '4px'
                              }}>
                                {person.label}
                              </span>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* C. ABSENCES */}
            {daily.absent.length > 0 && (
              <div style={{ padding:'20px', borderTop:'1px solid #f1f5f9', background:'#fff5f5' }}>
                <h3 style={{ fontSize:'0.9rem', textTransform:'uppercase', color:'#b91c1c', margin:'0 0 15px 0', display:'flex', alignItems:'center', gap:'6px' }}>
                  <AlertTriangle size={16} /> Absents ({daily.absent.length})
                </h3>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'12px' }}>
                  {daily.absent.map((a, i) => {
                    const style = getAbsenceStyle(a.type);
                    return (
                      <div key={i} style={{
                        display:'flex', flexDirection:'column', padding:'10px 14px',
                        background: 'white', borderRadius:'8px', borderLeft:`4px solid ${style.border}`,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <span style={{ fontWeight:'700', color:'#1e293b', fontSize:'0.9rem' }}>{a.name}</span>
                          {a.days > 1 && (
                            <span style={{
                              fontSize:'0.7rem',
                              fontWeight:'700',
                              color:'#64748b',
                              background:'#f1f5f9',
                              padding:'2px 6px',
                              borderRadius:'4px'
                            }}>
                              {a.days}j
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize:'0.75rem', fontWeight:'700', color: style.color, textTransform:'uppercase', marginTop:'2px' }}>
                          {style.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ACCÈS RAPIDES */}
          <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ background: '#eff6ff', padding: '8px', borderRadius: '8px', color: '#2563eb' }}><LayoutDashboard size={20} /></div>
              <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Accès Rapides</h2>
            </div>
            <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              <ActionCard icon={<FileClock size={28} />} title="Heures à valider" desc={`${stats.pendingHours} en attente`} onClick={() => navigate('/admin')} color="#f59e0b" />
              <ActionCard icon={<CalendarDays size={28} />} title="Suivi Congés" desc="Synthèse direction" onClick={() => navigate('/admin/leaves')} color="#ef4444" />
              <ActionCard icon={<CalendarRange size={28} />} title="Planning Hebdo" desc="Vue globale PDF" onClick={() => navigate('/admin/planning')} color="#0ea5e9" />
              <ActionCard icon={<FolderKanban size={28} />} title="Projets" desc="Codes affaires" onClick={() => navigate('/admin/projects')} color="#6366f1" />
              <ActionCard icon={<ShoppingCart size={28} />} title="Gestion Matériel" desc="Fournisseurs & Commandes" onClick={() => navigate('/admin/materials')} color="#f59e0b" />
              <ActionCard icon={<Archive size={28} />} title="Achats & BL" desc="Bons de livraison" onClick={() => navigate('/admin/purchases')} color="#10b981" />
              <ActionCard icon={<BarChart3 size={28} />} title="Comparateur Prix" desc="Prix fournisseurs" onClick={() => navigate('/admin/price-comparator')} color="#8b5cf6" />
              <ActionCard icon={<Receipt size={28} />} title="Facturation" desc="Factures clients" onClick={() => navigate('/admin/invoicing')} color="#7c3aed" />
            </div>
          </div>

        </div>

        {/* COLONNE DROITE (RENTABILITÉ + ADMIN) */}
        <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                <div style={{ background: '#ecfdf5', padding: '8px', borderRadius: '8px', color: '#10b981' }}><TrendingUp size={20} /></div>
                <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Rentabilité</h2>
              </div>
              <button onClick={() => navigate('/admin/reporting')} style={{ fontSize:'0.85rem', color:'#3b82f6', background:'transparent', border:'none', cursor:'pointer', fontWeight:'600' }}>Voir tout</button>
            </div>
            
            <div style={{ padding: '20px' }}>
              {projects.length === 0 ? (
                <p style={{color:'#94a3b8', textAlign:'center', fontStyle:'italic'}}>Aucune donnée disponible</p>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:'20px' }}>
                  {projects.map(p => {
                    let color = '#10b981'; // Vert
                    if (p.percentage > 80) color = '#f59e0b'; // Orange
                    if (p.percentage > 100) color = '#ef4444'; // Rouge
                    
                    return (
                      <div key={p.id}>
                        {/* NOM + CODE DU CHANTIER */}
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:'6px' }}>
                          <div style={{ display:'flex', flexDirection:'column' }}>
                             <span style={{ fontWeight:'700', color:'#1e293b', fontSize:'0.9rem' }}>{p.name}</span>
                             <span style={{ fontSize:'0.8rem', color:'#64748b' }}>{p.code}</span>
                          </div>
                          <span style={{ fontWeight:'700', color: color, fontSize:'0.9rem' }}>{p.percentage.toFixed(0)}%</span>
                        </div>
                        
                        <div style={{ width:'100%', height:'8px', background:'#f1f5f9', borderRadius:'4px', overflow:'hidden' }}>
                          <div style={{ width: `${Math.min(p.percentage, 100)}%`, height:'100%', background: color, borderRadius:'4px' }}></div>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', marginTop:'4px', fontSize:'0.75rem', color:'#64748b' }}>
                          <span>{p.consumed.toFixed(0)}h</span>
                          <span>Max: {p.budget}h</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ background: '#f3e8ff', padding: '8px', borderRadius: '8px', color: '#7c3aed' }}><Briefcase size={20} /></div>
              <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Administration</h2>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <ListActionRow icon={<Archive size={20} />} title="Mes Documents" desc="Archives des feuilles signées" onClick={() => navigate('/admin/documents')} />
              <ListActionRow icon={<BarChart3 size={20} />} title="Reporting & Paie" desc="Exports Excel, Rentabilité" onClick={() => navigate('/admin/reporting')} />
              <ListActionRow icon={<UserPlus size={20} />} title="Gestion Personnel" desc="Comptes, Codes PIN, Rôles" onClick={() => navigate('/admin/employees')} />
              <ListActionRow icon={<GraduationCap size={20} />} title="Formations" desc="Planifier les stages" onClick={() => navigate('/admin/training')} />
              {/* ✅ NOUVEAU BOUTON : AFFAIRES DIVERSES */}
              <ListActionRow icon={<FolderSearch size={20} />} title="Affaires Diverses" desc="Répartition JLO / GP" onClick={() => navigate('/admin/divers')} />
              <ListActionRow icon={<Bot size={20} />} title="Assistant IA" desc="Rapports & analyses" onClick={() => navigate('/admin/assistant')} />
              <ListActionRow icon={<Receipt size={20} />} title="Facturation Client" desc="Factures & paiements" onClick={() => navigate('/admin/invoicing')} />
            </div>
          </div>

        </div>

      </div>

      {/* STYLES ANIMATION */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
        .pulse-icon {
          animation: pulse 2s infinite;
        }
      `}</style>
    </div>
  );
}

// --- SOUS-COMPOSANTS ---

function StatCard({ title, value, icon, color, bg, loading, onClick, actionLabel, hideValue }: StatCardProps) {
  return (
    <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', transition: 'transform 0.2s', position: 'relative', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div style={{ background: bg, color: color, padding: '10px', borderRadius: '12px' }}>{icon}</div>
        {!hideValue && value > 0 && <span style={{ background: color, color: 'white', fontSize: '0.75rem', padding: '2px 8px', borderRadius: '10px', fontWeight: '700' }}>{value}</span>}
      </div>
      <div>
        {hideValue ? (
          <div style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b', lineHeight: '1.4' }}>{title}</div>
        ) : (
          <>
            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: '#0f172a', lineHeight: '1.2' }}>{loading ? '-' : value}</div>
            <div style={{ fontSize: '0.85rem', fontWeight: '500', color: '#64748b' }}>{title}</div>
          </>
        )}
      </div>
      <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px dashed #f1f5f9' }}>
        <button onClick={onClick} style={{ background: 'transparent', border: 'none', color: color, fontWeight: '600', cursor: 'pointer', padding: 0, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
          {actionLabel} <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function ActionCard({ icon, title, desc, onClick, color = '#3b82f6' }: ActionCardProps) {
  return (
    <div 
      onClick={onClick}
      style={{ 
        background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', 
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '15px', transition: 'all 0.2s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{ color: color, background: `${color}15`, padding: '10px', borderRadius: '10px' }}>{icon}</div>
      <div>
        <div style={{ fontWeight: '700', color: '#1e293b', fontSize:'0.95rem' }}>{title}</div>
        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{desc}</div>
      </div>
    </div>
  );
}

function ListActionRow({ icon, title, desc, onClick }: { icon: React.ReactNode, title: string, desc: string, onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '15px', padding: '12px', borderRadius: '10px',
        cursor: 'pointer', transition: 'all 0.2s', border: '1px solid transparent'
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}
    >
      <div style={{ color: '#475569', background: '#f1f5f9', padding: '10px', borderRadius: '8px' }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.95rem' }}>{title}</div>
        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{desc}</div>
      </div>
      <ArrowRight size={16} color="#cbd5e1" />
    </div>
  );
}

function AlertCard({ count, label, icon, color, bg, borderColor, onClick }: AlertCardProps) {
  return (
    <div onClick={onClick} style={{
      flex: '1 1 200px',
      background: 'white',
      border: '1px solid #e2e8f0',
      borderLeft: `5px solid ${borderColor}`,
      borderRadius: '12px',
      padding: '16px 20px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '15px',
      transition: 'all 0.2s',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    }} onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
      <div style={{ display:'flex', alignItems:'center', gap:'15px' }}>
        <div style={{ background: bg, color: color, padding: '10px', borderRadius: '50%' }}>{icon}</div>
        <div>
          <div style={{ fontWeight: '800', fontSize: '1.2rem', color: color }}>{count}</div>
          <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569' }}>{label}</div>
        </div>
      </div>
      <div style={{ color: '#cbd5e1' }}><ArrowRight size={20}/></div>
    </div>
  );
}